import { useCallback, useEffect, useState } from "react";

import { isApolloError } from "@apollo/client";
import { Flex, useDisclosure } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router";

import { InventoryChangeReason, ShelfChangeStatus } from "__graphql__/types";
import { PageName, ShelfChangeAction, ShelfChangeProgressState } from "analytics/events";
import { routes } from "config/routes";
import { Page } from "shared/components/Page";
import { SpinnerModal } from "shared/components/SpinnerModal";
import { useAnalytics } from "shared/hooks/useAnalytics";
import { useCustomToast } from "shared/hooks/useCustomToast";
import { useScan } from "shared/hooks/useScan/useScan";
import { useUpdateProductStockByDeltaAndMultipleReasonsMutation } from "shared/queries/inventoryEntry/inventoryEntry.generated";
import { Button } from "ui/Button/Button";
import { ScanIcon } from "ui/Icons/Icons";
import { NavigationHeader } from "ui/NavigationHeader/NavigationHeader";
import { TitleS } from "ui/Typography/Typography";

import { RescueBagDetailsCard } from "../components/RescueBagDetailsCard";
import { ShelfAlreadyAssignedModal } from "../components/ShelfAlreadyAssignedModal";
import { ShelfChangeDetailsCard } from "../components/ShelfChangeDetailsCard";
import { ShelfMismatchModal } from "../components/ShelfMismatchModal";
import {
  useFinishShelfChangeProposalMutation,
  useGetShelfChangeProposalByIdLazyQuery,
} from "../queries/shelfChanges.generated";

export function ShelfChangeProposal({ proposalId }: { proposalId: string }) {
  const navigate = useNavigate();
  const intl = useIntl();
  const { showToastUI } = useCustomToast();
  const { sendSegmentTrackEvent } = useAnalytics();

  const [wasJustCompleted, setWasJustCompleted] = useState(false);

  const {
    onOpen: onOpenShelfMismatchModal,
    isOpen: isShelfMismatchModalOpen,
    onClose: onCloseShelfMismatchModal,
  } = useDisclosure();

  const {
    onOpen: onOpenShelfAlreadyAssignedModal,
    isOpen: isShelfAlreadyAssignedModalOpen,
    onClose: onCloseShelfAlreadyAssignedModal,
  } = useDisclosure();

  const [getProposal, { data, loading, refetch }] = useGetShelfChangeProposalByIdLazyQuery({
    fetchPolicy: "network-only",
    nextFetchPolicy: "network-only",
    onError: () => {
      showToastUI({
        id: "shelf_change_fetch_error_toast_id",
        title: "components.shelf-changes.shelf-details-failed.title",
      });
      navigate(routes.inventory.shelfChanges.root);
    },
  });

  const [finishShelfChangeProposal, { loading: isFinishing }] =
    useFinishShelfChangeProposalMutation();

  const [updateProductStock, { loading: isUpdatingStock }] =
    useUpdateProductStockByDeltaAndMultipleReasonsMutation();

  const shelfChangeProposal = data?.getShelfChangeProposalById;

  // A proposal with no destination shelf is a delisting ("Rescue bag") task:
  // the product must be outbounded rather than relocated.
  const isRescueBag = Boolean(shelfChangeProposal) && !shelfChangeProposal?.shelfTo;
  const shelfStock = shelfChangeProposal?.product?.inventoryEntry?.stock?.shelf ?? 0;

  const [quantity, setQuantity] = useState(0);

  useEffect(() => {
    getProposal({ variables: { proposalId } });
  }, [proposalId, getProposal]);

  // Pre-fill the quantity to outbound with the full shelf stock once it loads.
  useEffect(() => {
    if (isRescueBag) setQuantity(shelfStock);
  }, [isRescueBag, shelfStock]);

  const handleScan = useCallback(
    async (scannedValue?: string) => {
      if (!shelfChangeProposal || isFinishing || !scannedValue) return;
      // Delisting tasks are confirmed via the "Confirm stock" button, not by scanning.
      if (isRescueBag) return;
      if (shelfChangeProposal?.status === ShelfChangeStatus.completed) return;

      if (scannedValue === shelfChangeProposal?.shelfTo) {
        try {
          await finishShelfChangeProposal({ variables: { proposalId } });
          sendSegmentTrackEvent("shelfChangeStateUpdated", {
            state: ShelfChangeProgressState.Finish,
            action: ShelfChangeAction.Scan,
            shelf_change_id: proposalId,
            product_sku: shelfChangeProposal.sku ?? "",
            product_name: shelfChangeProposal.product?.name ?? "",
            from_shelf_id: shelfChangeProposal.shelfFrom ?? "",
            to_shelf_id: shelfChangeProposal.shelfTo ?? "",
          });
          setWasJustCompleted(true);
        } catch (error: any) {
          if (
            isApolloError(error) &&
            error.graphQLErrors?.[0]?.extensions?.code === "SHELF_PROPOSAL_ALREADY_FINISHED"
          ) {
            onOpenShelfAlreadyAssignedModal();
            sendSegmentTrackEvent("inAppMessageShown", {
              screen_name: PageName.INVENTORY_SHELF_CHANGES_PAGE,
              component_name: "shelf_assigned",
              component_content: proposalId,
            });
          } else {
            showToastUI({
              id: "shelf_change_finish_error_toast_id",
              title: error?.message ?? "Unknown error",
            });
          }
        } finally {
          refetch();
        }
      } else {
        onOpenShelfMismatchModal();
        sendSegmentTrackEvent("inAppMessageShown", {
          screen_name: PageName.INVENTORY_SHELF_CHANGES_PAGE,
          component_name: "wrong_shelf",
          component_content: `${proposalId}::${shelfChangeProposal?.shelfTo}::${scannedValue}`,
        });
      }
    },
    [
      finishShelfChangeProposal,
      isFinishing,
      isRescueBag,
      onOpenShelfAlreadyAssignedModal,
      onOpenShelfMismatchModal,
      proposalId,
      refetch,
      sendSegmentTrackEvent,
      shelfChangeProposal,
      showToastUI,
    ],
  );

  useScan({ onScan: handleScan });

  const handleConfirmStock = useCallback(async () => {
    if (!shelfChangeProposal || !isRescueBag || isUpdatingStock || isFinishing) return;
    // When there is stock, a quantity is required. When stock is already 0 there is
    // nothing to outbound, so we skip the stock update and just finish the task.
    if (quantity <= 0 && shelfStock > 0) return;

    const { sku } = shelfChangeProposal;
    const reason = InventoryChangeReason.outbound_too_good_to_go;

    try {
      if (quantity > 0) {
        await updateProductStock({
          variables: {
            updateProductStockByDeltaAndMultipleReasonsInput: {
              sku,
              stockUpdates: [
                {
                  quantityDelta: -quantity,
                  reason,
                  transactionId: `${sku}:${reason}:${quantity}:${Date.now()}`,
                  stockCorrectionId: crypto.randomUUID(),
                },
              ],
            },
          },
        });
      }

      await finishShelfChangeProposal({ variables: { proposalId } });

      sendSegmentTrackEvent("shelfChangeStateUpdated", {
        state: ShelfChangeProgressState.Finish,
        action: ShelfChangeAction.Click,
        shelf_change_id: proposalId,
        product_sku: sku ?? "",
        product_name: shelfChangeProposal.product?.name ?? "",
        from_shelf_id: shelfChangeProposal.shelfFrom ?? "",
        to_shelf_id: shelfChangeProposal.shelfTo ?? "",
      });
      setWasJustCompleted(true);
    } catch (error: any) {
      if (
        isApolloError(error) &&
        error.graphQLErrors?.[0]?.extensions?.code === "SHELF_PROPOSAL_ALREADY_FINISHED"
      ) {
        onOpenShelfAlreadyAssignedModal();
        sendSegmentTrackEvent("inAppMessageShown", {
          screen_name: PageName.INVENTORY_SHELF_CHANGES_PAGE,
          component_name: "shelf_assigned",
          component_content: proposalId,
        });
      } else {
        showToastUI({
          id: "shelf_change_finish_error_toast_id",
          title: error?.message ?? "Unknown error",
        });
      }
    } finally {
      refetch();
    }
  }, [
    finishShelfChangeProposal,
    isFinishing,
    isRescueBag,
    isUpdatingStock,
    onOpenShelfAlreadyAssignedModal,
    proposalId,
    quantity,
    refetch,
    sendSegmentTrackEvent,
    shelfChangeProposal,
    shelfStock,
    showToastUI,
    updateProductStock,
  ]);

  const onClickGoBack = useCallback(() => {
    if (
      shelfChangeProposal &&
      shelfChangeProposal.status === ShelfChangeStatus.completed &&
      wasJustCompleted
    ) {
      sendSegmentTrackEvent("shelfChangeStateUpdated", {
        state: ShelfChangeProgressState.Finish,
        action: ShelfChangeAction.Click,
        shelf_change_id: proposalId,
        product_sku: shelfChangeProposal.sku ?? "",
        product_name: shelfChangeProposal.product?.name ?? "",
        from_shelf_id: shelfChangeProposal.shelfFrom ?? "",
        to_shelf_id: shelfChangeProposal.shelfTo ?? "",
      });
    }
    navigate(routes.inventory.shelfChanges.root);
  }, [navigate, proposalId, sendSegmentTrackEvent, shelfChangeProposal, wasJustCompleted]);

  return (
    <Page isFull isBgGrey h="100%">
      <NavigationHeader
        onClickGoBack={onClickGoBack}
        title={intl.formatMessage({
          id: "pages.inventory.shelf-change.title",
        })}
      />
      <Flex
        w="100%"
        h="100%"
        pt={isRescueBag ? 0 : "s200"}
        flex="1"
        direction="column"
        overflowY="scroll"
        mb="80px"
      >
        <SpinnerModal isOpen={loading || isFinishing || isUpdatingStock} />
        {shelfChangeProposal &&
          (isRescueBag ? (
            <RescueBagDetailsCard
              sku={shelfChangeProposal.sku}
              name={shelfChangeProposal.product?.name || null}
              imageUrl={shelfChangeProposal.product?.imageUrl || null}
              stock={shelfStock}
              status={shelfChangeProposal.status}
              quantity={quantity}
              onChangeQuantity={setQuantity}
            />
          ) : (
            <ShelfChangeDetailsCard
              proposalId={proposalId}
              sku={shelfChangeProposal.sku}
              shelfFrom={shelfChangeProposal.shelfFrom || null}
              shelfTo={shelfChangeProposal.shelfTo || null}
              name={shelfChangeProposal.product?.name || null}
              imageUrl={shelfChangeProposal.product?.imageUrl || null}
              numberOfShelfFacings={shelfChangeProposal.product?.numberOfShelfFacings || null}
              isShelvedInHandlingUnits={
                shelfChangeProposal.product?.isShelvedInHandlingUnits || null
              }
              inventoryEntry={
                shelfChangeProposal.product?.inventoryEntry ?? { stock: { shelf: 0 } }
              }
              status={shelfChangeProposal.status}
            />
          ))}
      </Flex>
      {shelfChangeProposal?.status === ShelfChangeStatus.open && isRescueBag && (
        <Flex w="100%" pos="fixed" bottom={0} p="s200">
          <Button
            variant="primary"
            isFullWidth
            onClick={handleConfirmStock}
            isLoading={isUpdatingStock || isFinishing}
            disabled={shelfStock > 0 && quantity <= 0}
          >
            {intl.formatMessage({
              id:
                shelfStock === 0
                  ? "pages.inventory.shelf-changes.finish-shelf-change"
                  : "pages.inventory.shelf-changes.rescue-bag.confirm-stock",
            })}
          </Button>
        </Flex>
      )}
      {shelfChangeProposal?.status === ShelfChangeStatus.open && !isRescueBag && (
        <Flex
          w="100%"
          h="80px"
          justify="center"
          align="center"
          px="s200"
          pos="fixed"
          gap="s100"
          bottom={0}
        >
          <TitleS>
            {intl.formatMessage({
              id: "pages.inventory.shelf-changes.scan-new-shelf-to-finish",
            })}
          </TitleS>
          <ScanIcon h="24px" w="24px" />
        </Flex>
      )}
      {shelfChangeProposal?.status === ShelfChangeStatus.completed && wasJustCompleted && (
        <Flex w="100%" pos="fixed" bottom={0} p="s200">
          <Button variant="primary" isFullWidth onClick={onClickGoBack}>
            {intl.formatMessage({
              id: "pages.inventory.shelf-changes.finish-shelf-change",
            })}
          </Button>
        </Flex>
      )}
      {isShelfMismatchModalOpen && <ShelfMismatchModal onClose={onCloseShelfMismatchModal} />}
      {isShelfAlreadyAssignedModalOpen && (
        <ShelfAlreadyAssignedModal onClose={onCloseShelfAlreadyAssignedModal} />
      )}
    </Page>
  );
}

export function ShelfChangeDetailsPage() {
  const navigate = useNavigate();

  const { proposalId } = useParams();

  if (!proposalId) {
    navigate(routes.inventory.shelfChanges.root);
    return null;
  }

  return <ShelfChangeProposal proposalId={proposalId} />;
}
